import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';
import AuthService from './AuthService';
import { v4 as uuidv4 } from 'uuid';
import { LP_TRACE_ID } from '@/constants/constants';

axiosRetry(axios, { retries: 3, retryDelay: axiosRetry.exponentialDelay });

class ApiService {
  inFlightRequests: Map<string, AbortController> = new Map();

  constructor(private authService: typeof AuthService) {}

  private get base() {
    return (
      import.meta.env.VITE_PROXY_URL || import.meta.env.VITE_BASE_API_URL || ''
    );
  }

  private async getHeaders() {
    const token = await this.authService.getToken();
    const headers = { [LP_TRACE_ID]: uuidv4() };
    if (token) {
      return { ...headers, Authorization: `Bearer ${token}` };
    }
    return headers;
  }

  private cancelAndCache(key: string) {
    if (this.inFlightRequests.has(key)) {
      this.inFlightRequests.get(key)?.abort();
    }

    const controller = new AbortController();
    this.inFlightRequests.set(key, controller);
    return controller;
  }

  cancelByKey(key: string) {
    this.inFlightRequests.get(key)?.abort();
    this.inFlightRequests.delete(key);
  }

  cancelAll() {
    this.inFlightRequests.forEach((controller) => controller.abort());
    this.inFlightRequests.clear();
  }

  hasInflightRequest(key: string) {
    return this.inFlightRequests.has(key);
  }

  private async handleInflight<T>(
    response: Promise<AxiosResponse<T>>,
    key: string,
  ) {
    return response
      .then((res) => {
        this.inFlightRequests.delete(key);
        return res;
      })
      .catch((err: AxiosError) => {
        if (err.code === AxiosError.ERR_CANCELED) {
          throw err;
        }
        this.inFlightRequests.delete(key);
        throw err;
      });
  }

  async get<T>(
    path: string,
    key: string,
    params?: any,
    config?: AxiosRequestConfig,
    ignoreBase: boolean = false,
    baseOverride?: string,
  ) {
    const controller = this.cancelAndCache(key);
    const authHeader = await this.getHeaders();
    const currentBaseUrl = baseOverride ? baseOverride : this.base;
    const baseURL = ignoreBase ? '' : currentBaseUrl;
    return this.handleInflight<T>(
      axios.get<T>(`${baseURL}/${path}`, {
        ...config,
        headers: authHeader,
        signal: controller.signal,
        params,
      }),
      key,
    );
  }

  async post<T>(
    path: string,
    body: any,
    key: string,
    headers?: any,
    config?: AxiosRequestConfig,
    baseOverride?: string,
  ) {
    const controller = this.cancelAndCache(key);
    const authHeader = await this.getHeaders();
    const baseUrl = baseOverride ? baseOverride : this.base;
    return this.handleInflight<T>(
      axios.post<T>(`${baseUrl}/${path}`, body, {
        headers: {
          ...authHeader,
          ...headers,
        },
        signal: controller.signal,
        ...config,
      }),
      key,
    );
  }

  async put<T>(
    path: string,
    body: any,
    key: string,
    headers?: any,
    config?: AxiosRequestConfig,
    baseOverride?: string,
  ) {
    const controller = this.cancelAndCache(key);
    const authHeader = await this.getHeaders();
    const baseUrl = baseOverride ? baseOverride : this.base;
    return this.handleInflight<T>(
      axios.put<T>(`${baseUrl}/${path}`, body, {
        headers: {
          ...headers,
          ...authHeader,
        },
        signal: controller.signal,
        ...config,
      }),
      key,
    );
  }

  async patch<T>(
    path: string,
    body: any,
    key: string,
    config?: AxiosRequestConfig,
    baseOverride?: string,
  ) {
    const controller = this.cancelAndCache(key);
    const authHeader = await this.getHeaders();
    const baseUrl = baseOverride ? baseOverride : this.base;
    return this.handleInflight<T>(
      axios.patch<T>(`${baseUrl}/${path}`, body, {
        headers: authHeader,
        signal: controller.signal,
        ...config,
      }),
      key,
    );
  }

  async delete<T>(
    path: string,
    key: string,
    params?: any,
    body?: any,
    headers?: any,
    config?: AxiosRequestConfig,
    baseOverride?: string,
  ) {
    const controller = this.cancelAndCache(key);
    const authHeader = await this.getHeaders();
    const baseUrl = baseOverride ? baseOverride : this.base;
    return this.handleInflight(
      axios.delete<T>(`${baseUrl}/${path}`, {
        headers: {
          ...headers,
          ...authHeader,
        },
        signal: controller.signal,
        params,
        data: body,
        ...config,
      }),
      key,
    );
  }
}

export default new ApiService(AuthService);
